/**
 * Runtime Arabic translation helpers for POS-synced catalog display (Foodics / TabSense / Ninja).
 * Fallback text is shown in read-only UI only — never written to the database.
 */

export const ARABIC_FALLBACK_TITLE = "الترجمة العربية غير متوفرة";
export const ARABIC_FALLBACK_DESCRIPTION = "الوصف العربي غير متوفر";

export const UI_FALLBACK_TITLE_EN = "Arabic translation not available";
export const UI_FALLBACK_DESCRIPTION_EN = "Arabic description not available";

const UI_FALLBACK_TITLE_AR = ARABIC_FALLBACK_TITLE;
const UI_FALLBACK_DESCRIPTION_AR = ARABIC_FALLBACK_DESCRIPTION;

const LEGACY_PLACEHOLDER_EXACT = new Set([
  "غير محدد",
  "لا يوجد وصف متاح",
]);

const LEGACY_PLACEHOLDER_PATTERNS = [
  /^منتج\d+$/,
  /^إضافة\d+$/,
  /^خيار\d+$/,
];

const ARABIC_LETTER_RE = /[\u0600-\u06FF]/;
const LATIN_LETTER_RE = /[A-Za-z]/;
const DIGIT_RE = /\d/;

function normalizeValue(value) {
  if (value == null) return "";
  return String(value).trim();
}

/**
 * Missing when empty, a known POS placeholder, or Latin copied into the Arabic slot.
 * Numeric-only values are allowed (same as product AR field validation) and are not missing.
 */
export function isArabicTranslationMissing(value) {
  const trimmed = normalizeValue(value);
  if (!trimmed) return true;
  if (LEGACY_PLACEHOLDER_EXACT.has(trimmed)) return true;
  if (LEGACY_PLACEHOLDER_PATTERNS.some((re) => re.test(trimmed))) return true;
  if (ARABIC_LETTER_RE.test(trimmed)) return false;
  if (LATIN_LETTER_RE.test(trimmed)) return true;
  if (DIGIT_RE.test(trimmed)) return false;
  return true;
}

function getUiFallback(field, isArabicUi) {
  if (isArabicUi) {
    return field === "description" ? UI_FALLBACK_DESCRIPTION_AR : UI_FALLBACK_TITLE_AR;
  }
  return field === "description" ? UI_FALLBACK_DESCRIPTION_EN : UI_FALLBACK_TITLE_EN;
}

/**
 * Display helper for read-only catalog views.
 * @returns {{ text: string, isMissingTranslation: boolean }}
 */
export function getArabicDisplayValue({ ar, en, field = "title", isArabicUi = false }) {
  const stored = normalizeValue(ar);
  if (!isArabicTranslationMissing(stored)) {
    return { text: stored, isMissingTranslation: false };
  }
  return {
    text: getUiFallback(field, isArabicUi),
    isMissingTranslation: true,
  };
}

/**
 * Bilingual display: current UI language first, with missing-Arabic flag when showing Arabic slot.
 */
export function getLocalizedDisplayText(enVal, arVal, isArabicUi, field = "title") {
  const en = normalizeValue(enVal);
  const arResult = getArabicDisplayValue({ ar: arVal, en, field, isArabicUi });
  if (isArabicUi) {
    return {
      text: arResult.text || en,
      isMissingTranslation: arResult.isMissingTranslation,
    };
  }
  if (en) {
    return { text: en, isMissingTranslation: arResult.isMissingTranslation };
  }
  return { text: arResult.text, isMissingTranslation: arResult.isMissingTranslation };
}
